import React, { useRef, useEffect, useState } from 'react'
import styles from './ProspectSetup.module.scss'
import clsx from 'clsx'
import { useFormik } from 'formik'
import {
  AccordionSection,
  ImagePlaceholderFillIcon,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  TextArea,
  TextInput,
  Checkbox,
  PencilIcon,
  EyeVisibleColorIcon,
  EmailTemplateEditorModal,
  PSPromoCodeModal,
  LoadingOverlay,
  HelpModal,
} from 'components'
import * as Yup from 'yup'
import { CAMPAIGN_STEP, PATH, MESSAGE, ERR_CODE } from 'utils/constants'
import { useStateValue } from 'state'
import { toast } from 'react-toastify'
import _ from 'lodash'

const ProspectSetup = ({
  isLocked = false,
  step: initStep,
  onBack = () => null,
  onNext = () => null,
  onSave = () => null,
  data: initData = {},
  campaignId = null,
  campaignSlug = '',
  promocode = '',
  setDirty = () => null,
  saveAndClose = () => null,
}) => {
  const invitationsRef = useRef(null)
  const emailsRef = useRef(null)
  const {
    state: {
      camp: { activeCamp },
      help,
    },
    action: { updateProspectSetup, getEmailTemplate, popupError },
  } = useStateValue()
  const [isSubmitted, setSubmitted] = useState(false)
  const [isNext, setNext] = useState(false)
  const [isStayHere, setIsStayHere] = useState(false)
  const [isEmailEditorOpened, setEmailEditorOpened] = useState(false)
  const [emailTemplateToEdit, setEmailTemplateToEdit] = useState({})
  const [isLandingPagePreview, setLandingPagePreview] = useState(false)
  const [showPromoCode, setShowPromoCode] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [, setSectionOpened] = useState('invitations')
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    setDirty(isDirty)
  }, [isDirty, setDirty])

  const showPopup = (url) => {
    let opened = window.open(url, '_blank')
    if (opened) {
      opened.focus()
      return
    }

    popupError({
      message: JSON.stringify({
        code: ERR_CODE.POPUP_BLOCKED,
        message:
          'Please allow popups. This preview will not work without popups.',
      }),
    })
  }

  const isValidUrl = (url) => {
    try {
      new URL(url)
    } catch (e) {
      return false
    }
    return true
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      landingBgImgURL: null,
      landingHeading: '',
      landingSubheading: '',
      linkToWebsite: '',
      learnMoreBtnTxt: '',
      link1: '',
      learnMoreSubheading: '',
      learnMorePopupHeading: '',
      learnMorePopupBtnTxt: '',
      // enrollHeading: '',
      enrollSubheading: '',
      enrollBtnTxt: '',
      linkToRegistrationPage: '',
      enrollPopupHeading: '',
      enrollPopupBtnTxt: '',
      enrollInstructions: '',
      ...initData,
    },
    validationSchema: Yup.object({
      landingBgImgURL: Yup.mixed().required(
        'Landing page background image is required.'
      ),
      landingHeading: Yup.string()
        .required('Header is required.')
        .max(100, 'Max length is 100.'),
      landingSubheading: Yup.string().max(250, 'Max length is 250.'),
      linkToWebsite: Yup.string()
        .required('Link is required.')
        .test('linkToWebsite', 'URL is not valid', (value) =>
          isValidUrl(value)
        ),
      link1: Yup.string()
        .required('Link text is required.')
        .max(20, 'Max length is 20.'),
      learnMoreBtnTxt: Yup.string()
        .required('Button text is required.')
        .max(36, 'Max length is 36.'),
      learnMoreSubheading: Yup.string()
        .required('Sub-heading is required.')
        .max(120, 'Max length is 120.'),
      learnMorePopupHeading: Yup.string()
        .required('Heading is required.')
        .max(120, 'Max length is 120.'),
      learnMorePopupBtnTxt: Yup.string()
        .required('Button text is required.')
        .max(55, 'Max length is 55.'),
      enrollPopupHeading: Yup.string()
        .required('Heading is required.')
        .max(70, 'Max length is 70.'),
      // enrollHeading: Yup.string()
      //   .required('Sub-heading is required.')
      //   .max(65, 'Max length is 65.'),
      enrollSubheading: Yup.string().max(120, 'Max length is 120.'),
      enrollBtnTxt: Yup.string()
        .required('Button text is required.')
        .max(36, 'Max length is 36.'),
      linkToRegistrationPage: Yup.string()
        .required('Registration URL is required.')
        .test('linkToRegistrationPage', 'URL is not valid', (value) =>
          isValidUrl(value)
        ),
      enrollPopupBtnTxt: Yup.string()
        .required('Button text is required.')
        .max(25, 'Max length is 25.'),
      enrollInstructions: Yup.string()
        .required('Registration Instructions are required.')
        .max(120, 'Max length is 120.'),
    }),
    onSubmit: (args) => {
      // if (isLocked)
      //   return isNext ? onNext() : navigate({ pathname: PATH.CAMPAIGNS })

      setLoading(true)

      if (isDirty)
        updateProspectSetup({ ...args, campaignId }, (prospectSetup) => {
          setLoading(false)

          if (isNext) onNext(prospectSetup)
          else if (isStayHere) onSave(prospectSetup)
          else saveAndClose()
        })
      else {
        setLoading(false)
        if (isNext) onNext(initData)
        else if (isStayHere) onSave(initData)
        else saveAndClose()
      }
    },
  })

  useEffect(() => {
    setIsDirty(
      !_.isEqual(
        _.omit(formik.values, [
          'created_at',
          'id',
          'templateVars',
          'updated_at',
        ]),
        _.omit(initData, ['created_at', 'id', 'templateVars', 'updated_at'])
      )
    )
  }, [formik, initData])

  useEffect(() => {
    if (initStep === CAMPAIGN_STEP.PROSPECT_SETUP__EMAILS)
      emailsRef.current.scrollIntoView({ behavior: 'smooth' })
    else window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [initStep])

  useEffect(() => {
    if (!isSubmitted) return

    if (_.isEmpty(formik.touched) || _.isEmpty(formik.errors)) return

    const toastErrors = (errors) =>
      _.values(errors).forEach((error) => {
        if (typeof error === 'string') toast.error(error)
        else toastErrors(error)
      })

    toastErrors(formik.errors)

    setSubmitted(false)
    // eslint-disable-next-line
  }, [isSubmitted])

  useEffect(() => {
    if (!isLandingPagePreview) return

    const {
      landingBgImgURL,
      landingHeading,
      landingSubheading,
      linkToWebsite,
      learnMoreBtnTxt,
      link1,
      learnMoreSubheading,
      learnMorePopupHeading,
      learnMorePopupBtnTxt,
      enrollPopupHeading,
      enrollPopupBtnTxt,
      enrollSubheading,
      enrollBtnTxt,
      linkToRegistrationPage,
      enrollInstructions,
    } = formik.values

    if (
      (formik.touched.landingBgImgURL && formik.errors.landingBgImgURL) ||
      !landingBgImgURL
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.landingHeading && formik.errors.landingHeading) ||
      !landingHeading
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.landingSubheading && formik.errors.landingSubheading) ||
      !landingSubheading
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.linkToWebsite && formik.errors.linkToWebsite) ||
      !linkToWebsite
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.learnMoreBtnTxt && formik.errors.learnMoreBtnTxt) ||
      !learnMoreBtnTxt
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.enrollSubheading && formik.errors.enrollSubheading) ||
      !enrollSubheading
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.enrollBtnTxt && formik.errors.enrollBtnTxt) ||
      !enrollBtnTxt
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.linkToRegistrationPage &&
        formik.errors.linkToRegistrationPage) ||
      !linkToRegistrationPage
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.enrollInstructions && formik.errors.enrollInstructions) ||
      !enrollInstructions
    ) {
      setLandingPagePreview(false)
      return
    }
    if ((formik.touched.link1 && formik.errors.link1) || !link1) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.learnMoreSubheading &&
        formik.errors.learnMoreSubheading) ||
      !learnMoreSubheading
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.learnMorePopupHeading &&
        formik.errors.learnMorePopupHeading) ||
      !learnMorePopupHeading
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.learnMorePopupBtnTxt &&
        formik.errors.learnMorePopupBtnTxt) ||
      !learnMorePopupBtnTxt
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.enrollPopupHeading && formik.errors.enrollPopupHeading) ||
      !enrollPopupHeading
    ) {
      setLandingPagePreview(false)
      return
    }
    if (
      (formik.touched.enrollPopupBtnTxt && formik.errors.enrollPopupBtnTxt) ||
      !enrollPopupBtnTxt
    ) {
      setLandingPagePreview(false)
      return
    }

    updateProspectSetup(
      {
        id: formik.values.id,
        campaignId,
        landingBgImgURL,
        landingHeading,
        landingSubheading,
        linkToWebsite,
        learnMoreBtnTxt,
        link1,
        learnMoreSubheading,
        learnMorePopupHeading,
        learnMorePopupBtnTxt,
        enrollPopupHeading,
        enrollPopupBtnTxt,
        enrollSubheading,
        enrollBtnTxt,
        linkToRegistrationPage,
        enrollInstructions,
      },
      () => {
        setLandingPagePreview(false)
        showPopup(
          `${process.env.REACT_APP_SHARE_HOST}/p/${campaignSlug}?preview=true`
        )
      }
    )
    // eslint-disable-next-line
  }, [isLandingPagePreview])

  const handleClickLandingPagePreview = () => {
    formik.setFieldTouched('landingBgImgURL', true)
    formik.setFieldTouched('landingHeading', true)
    formik.setFieldTouched('landingSubheading', true)
    formik.setFieldTouched('learnMoreBtnTxt', true)
    formik.setFieldTouched('learnMoreSubheading', true)
    formik.setFieldTouched('enrollSubheading', true)
    formik.setFieldTouched('enrollBtnTxt', true)

    formik.setFieldTouched('linkToRegistrationPage', true)
    formik.setFieldTouched('enrollPopupBtnTxt', true)
    formik.setFieldTouched('enrollInstructions', true)
    formik.setFieldTouched('enrollPopupHeading', true)

    formik.setFieldTouched('learnMorePopupHeading', true)
    formik.setFieldTouched('learnMorePopupBtnTxt', true)
    formik.setFieldTouched('linkToWebsite', true)
    formik.setFieldTouched('link1', true)
    if (
      (formik.touched.landingBgImgURL && formik.errors.landingBgImgURL) ||
      !formik.values.landingBgImgURL
    )
      return
    if (
      (formik.touched.landingHeading && formik.errors.landingHeading) ||
      !formik.values.landingHeading
    )
      return
    if (
      (formik.touched.landingSubheading && formik.errors.landingSubheading) ||
      !formik.values.landingSubheading
    )
      return
    if (
      (formik.touched.learnMoreBtnTxt && formik.errors.learnMoreBtnTxt) ||
      !formik.values.learnMoreBtnTxt
    )
      return
    if (
      (formik.touched.learnMoreSubheading &&
        formik.errors.learnMoreSubheading) ||
      !formik.values.learnMoreSubheading
    )
      return
    if (
      (formik.touched.enrollSubheading && formik.errors.enrollSubheading) ||
      !formik.values.enrollSubheading
    )
      return
    if (
      (formik.touched.enrollBtnTxt && formik.errors.enrollBtnTxt) ||
      !formik.values.enrollBtnTxt
    )
      return

    // enroll popup
    if (
      (formik.touched.linkToRegistrationPage &&
        formik.errors.linkToRegistrationPage) ||
      !formik.values.linkToRegistrationPage
    )
      return
    if (
      (formik.touched.enrollPopupBtnTxt && formik.errors.enrollPopupBtnTxt) ||
      !formik.values.enrollPopupBtnTxt
    )
      return
    if (
      (formik.touched.enrollInstructions && formik.errors.enrollInstructions) ||
      !formik.values.enrollInstructions
    )
      return
    if (
      (formik.touched.enrollPopupHeading && formik.errors.enrollPopupHeading) ||
      !formik.values.enrollPopupHeading
    )
      return

    // learnmore popup
    if (
      (formik.touched.learnMorePopupHeading &&
        formik.errors.learnMorePopupHeading) ||
      !formik.values.learnMorePopupHeading
    )
      return
    if (
      (formik.touched.learnMorePopupBtnTxt &&
        formik.errors.learnMorePopupBtnTxt) ||
      !formik.values.learnMorePopupBtnTxt
    )
      return
    if (
      (formik.touched.linkToWebsite && formik.errors.linkToWebsite) ||
      !formik.values.linkToWebsite
    )
      return

    if ((formik.touched.link1 && formik.errors.link1) || !formik.values.link1)
      return
    setLandingPagePreview(true)
  }

  const handleClickPromocodePreview = () => {
    formik.setFieldTouched('linkToRegistrationPage', true)
    formik.setFieldTouched('enrollPopupBtnTxt', true)
    formik.setFieldTouched('enrollInstructions', true)
    formik.setFieldTouched('enrollPopupHeading', true)
    if (
      (formik.touched.linkToRegistrationPage &&
        formik.errors.linkToRegistrationPage) ||
      !formik.values.linkToRegistrationPage
    )
      return
    if (
      (formik.touched.enrollPopupBtnTxt && formik.errors.enrollPopupBtnTxt) ||
      !formik.values.enrollPopupBtnTxt
    )
      return
    if (
      (formik.touched.enrollInstructions && formik.errors.enrollInstructions) ||
      !formik.values.enrollInstructions
    )
      return
    if (
      (formik.touched.enrollPopupHeading && formik.errors.enrollPopupHeading) ||
      !formik.values.enrollPopupHeading
    )
      return

    setShowPromoCode(true)
  }

  const handleClickSubmit = (isNext) => {
    setNext(isNext)
    setIsStayHere(false)
    setSubmitted(true)
    formik.handleSubmit()
  }

  const handleSubmitOnly = () => {
    setIsStayHere(true)
    setSubmitted(true)
    formik.handleSubmit()
  }

  const handleClickEmailEditor = (template) =>
    getEmailTemplate(
      campaignId,
      template.TYPE,
      ({ htmlContentTemplate, templateVars }) => {
        setEmailTemplateToEdit({
          title: template.LABEL,
          type: template.TYPE,
          data: {
            subject: htmlContentTemplate.subject,
            heading: htmlContentTemplate.heading,
            subheading: htmlContentTemplate.subheading,
            content: htmlContentTemplate.text,
          },
          optionalTags: htmlContentTemplate.optionalTags,
          requiredTags: htmlContentTemplate.requiredTags,
          templateVars,
        })
        setEmailEditorOpened(true)
      }
    )
  const [activeFieldName, setActiveFieldName] = useState(null)
  const [activeElement, setActiveElement] = useState(null)

  const handleFocus = (event, fieldName) => {
    setActiveElement(event)
    setActiveFieldName(fieldName)
  }

  const insertTag = (tag) => {
    if (activeElement) {
      const start = activeElement.selectionStart
      const end = activeElement.selectionEnd
      const before = activeElement.value.substring(0, start)
      const after = activeElement.value.substring(end)

      const newValue = before + `{{${tag}}}` + after

      formik.setFieldValue(activeFieldName, newValue)

      setTimeout(() => {
        activeElement.focus()
        activeElement.selectionStart = activeElement.selectionEnd =
          start + tag.length + 4
      }, 0)
    } else {
      console.warn('No active element found')
    }
  }

  const tags = [
    'camp-name',
    'ambassador-first-name',
    'ambassador-last-name',
    'prospect-reward-description',
  ]
  return (
    <>
      <div className={styles.section}>
        <form className={styles.form}>
          <fieldset
            ref={invitationsRef}
            className={styles.section__invitations}
          >
            <div className={styles.profile_container}>
              <h4 className={styles.section__title}>Invitation</h4>
              {help.find((h) => h.tag === 'prospect_setup_invitation') && (
                <HelpModal
                  className={styles.helpModal}
                  help={help.find((h) => h.tag === 'prospect_setup_invitation')}
                >
                  <p>
                    {
                      help.find((h) => h.tag === 'prospect_setup_invitation')
                        ?.text
                    }
                  </p>
                </HelpModal>
              )}
            </div>
            <AccordionSection
              className={styles.accordion_section}
              title="Landing Page"
              description="Invite Prospect to Enroll"
              opened={true}
              // opened={sectionOpened === 'invitations'}
              onActionToOpen={() => setSectionOpened('invitations')}
              onActionToClose={() => setSectionOpened(null)}
              contentHeightScale={2}
              help={help.find((h) => h.tag === 'prospect_setup_landing')}
              helpChildren={
                <p>
                  {help.find((h) => h.tag === 'prospect_setup_landing')?.text}
                </p>
              }
            >
              <div className={styles.form__row}>
                <div className={styles.field__bgImg__wrapper}>
                  <div
                    className={clsx(
                      styles.field__bgImg__preview,
                      formik.touched.landingBgImgURL &&
                        formik.errors.landingBgImgURL &&
                        styles['field__bgImg__preview--error']
                    )}
                  >
                    {formik.values.landingBgImgURL ? (
                      <img
                        src={
                          typeof formik.values.landingBgImgURL === 'string'
                            ? formik.values.landingBgImgURL
                            : URL.createObjectURL(formik.values.landingBgImgURL)
                        }
                        alt="Landing page background"
                      />
                    ) : (
                      <ImagePlaceholderFillIcon
                        className={styles.field__bgImg__placeholder}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.form__row}>
                <div className={styles.form__col}>
                  <div className={styles.tags}>
                    <span className={styles.tags__optional}>Optional:</span>
                    <p
                      className={clsx(
                        styles.field__merge_tags__wrapper,
                        styles.tags__optional__tag
                      )}
                    >
                      {tags?.map((tag, tIndex) => (
                        <button
                          key={tIndex}
                          type="button"
                          className={clsx(
                            styles.field__merge_tag,
                            styles['field__merge_tag--optional']
                          )}
                          dangerouslySetInnerHTML={{
                            __html: `{{${tag}}}`,
                          }}
                          onClick={() => insertTag(`${tag}`)}
                        />
                      ))}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.form__row}>
                <div className={styles.form__col}>
                  <TextArea
                    tabIndex="1"
                    rows="2"
                    label="Header"
                    disabled={isLocked}
                    placeholder="Header"
                    value={formik.values.landingHeading}
                    onChange={(value) =>
                      formik.setFieldValue('landingHeading', value)
                    }
                    {...(formik.touched.landingHeading &&
                    formik.errors.landingHeading
                      ? {
                          isError: true,
                          captionText: formik.errors.landingHeading,
                        }
                      : {
                          captionText: `${formik.values.landingHeading.length}/100`,
                        })}
                    handleFocus={(event) =>
                      handleFocus(event, 'landingHeading')
                    }
                  />
                </div>

                <div className={styles.form__col}>
                  <TextArea
                    tabIndex="2"
                    rows="2"
                    label="Sub Header"
                    disabled={isLocked}
                    placeholder="Sub Header"
                    value={formik.values.landingSubheading}
                    onChange={(value) =>
                      formik.setFieldValue('landingSubheading', value)
                    }
                    {...(formik.touched.landingSubheading &&
                    formik.errors.landingSubheading
                      ? {
                          isError: true,
                          captionText: formik.errors.landingSubheading,
                        }
                      : {
                          captionText: `${formik.values.landingSubheading.length}/250`,
                        })}
                    handleFocus={(event) =>
                      handleFocus(event, 'landingSubheading')
                    }
                  />
                </div>
              </div>

              <div className={styles['form__row--heading']}></div>
              <div className={styles.form__row}>
                <div className={styles.form__col}>
                  <TextInput
                    tabIndex="9"
                    rows="1"
                    label="Link Text"
                    disabled={isLocked}
                    placeholder="Enter custom link title here"
                    value={formik.values.link1}
                    onChange={(value) => formik.setFieldValue('link1', value)}
                    help={help.find(
                      (h) => h.tag === 'prospect_setup_link_text'
                    )}
                    helpChildren={
                      <p>
                        {
                          help.find((h) => h.tag === 'prospect_setup_link_text')
                            ?.text
                        }
                      </p>
                    }
                    {...(formik.touched.link1 && formik.errors.link1
                      ? {
                          isError: true,
                          captionText: formik.errors.link1,
                        }
                      : {
                          captionText: `${formik.values.link1.length}/20`,
                        })}
                    handleFocus={(event) => handleFocus(event, 'link1')}
                  />
                </div>

                <div className={styles.form__col}>
                  <TextInput
                    tabIndex="3"
                    label="Link"
                    disabled={isLocked}
                    placeholder="Enter custom link here"
                    value={formik.values.linkToWebsite}
                    onChange={(value) => {
                      formik.setFieldValue('linkToWebsite', value)
                    }}
                    help={help.find((h) => h.tag === 'prospect_setup_link')}
                    helpChildren={
                      <p>
                        {
                          help.find((h) => h.tag === 'prospect_setup_link')
                            ?.text
                        }
                      </p>
                    }
                    {...(formik.touched.linkToWebsite &&
                    formik.errors.linkToWebsite
                      ? {
                          isError: true,
                          captionText: formik.errors.linkToWebsite,
                        }
                      : {})}
                  />
                </div>
              </div>
              <div className={styles.form__row}>
                <div className={styles.form__col}></div>
                <div className={styles.form__col}>
                  <div className={styles.landing__preview__container}>
                    <div className={styles.landing__preview__button__wrapper}>
                      <SecondaryButton
                        className={styles.landing__preview__button}
                        label="Preview"
                        onClick={handleClickLandingPagePreview}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </AccordionSection>
            <AccordionSection
              className={styles.accordion_section}
              title="Claim Code Pop Up"
              opened={true}
              // opened={sectionOpened === 'claim-code'}
              onActionToOpen={() => setSectionOpened('claim-code')}
              onActionToClose={() => setSectionOpened(null)}
              help={help.find((h) => h.tag === 'prospect_setup_claim_code')}
              helpChildren={
                <p>
                  {
                    help.find((h) => h.tag === 'prospect_setup_claim_code')
                      ?.text
                  }
                </p>
              }
            >
              <div className={styles.form__row}>
                <div className={styles.form__col}>
                  <TextInput
                    tabIndex="9"
                    rows="1"
                    label="Header"
                    disabled={isLocked}
                    placeholder="Header"
                    value={formik.values.enrollPopupHeading}
                    onChange={(value) =>
                      formik.setFieldValue('enrollPopupHeading', value)
                    }
                    {...(formik.touched.enrollPopupHeading &&
                    formik.errors.enrollPopupHeading
                      ? {
                          isError: true,
                          captionText: formik.errors.enrollPopupHeading,
                        }
                      : {
                          captionText: `${formik.values.enrollPopupHeading.length}/70`,
                        })}
                  />
                  <TextInput
                    tabIndex="11"
                    label="Registration URL"
                    disabled={isLocked}
                    placeholder="http//:"
                    value={formik.values.linkToRegistrationPage}
                    onChange={(value) =>
                      formik.setFieldValue('linkToRegistrationPage', value)
                    }
                    {...(formik.touched.linkToRegistrationPage &&
                    formik.errors.linkToRegistrationPage
                      ? {
                          isError: true,
                          captionText: formik.errors.linkToRegistrationPage,
                        }
                      : {})}
                  />
                  <TextInput
                    tabIndex="11"
                    label="Claim Code"
                    disabled={true}
                    placeholder={promocode ?? ''}
                    value={promocode}
                  />
                </div>

                <div className={styles.form__col}>
                  <TextInput
                    tabIndex="11"
                    label="Button Text"
                    disabled={isLocked}
                    placeholder="Registration Page"
                    value={formik.values.enrollPopupBtnTxt}
                    onChange={(value) =>
                      formik.setFieldValue('enrollPopupBtnTxt', value)
                    }
                    {...(formik.touched.enrollPopupBtnTxt &&
                    formik.errors.enrollPopupBtnTxt
                      ? {
                          isError: true,
                          captionText: formik.errors.enrollPopupBtnTxt,
                        }
                      : {
                          captionText: `${formik.values.enrollPopupBtnTxt.length}/25`,
                        })}
                  />
                  <TextArea
                    tabIndex="12"
                    rows={6}
                    label="Registration Instructions"
                    disabled={isLocked}
                    placeholder="Copy and insert this code on the camp registration form"
                    value={formik.values.enrollInstructions}
                    onChange={(value) =>
                      formik.setFieldValue('enrollInstructions', value)
                    }
                    {...(formik.touched.enrollInstructions &&
                    formik.errors.enrollInstructions
                      ? {
                          isError: true,
                          captionText: formik.errors.enrollInstructions,
                        }
                      : {
                          captionText: `${formik.values.enrollInstructions.length}/120`,
                        })}
                  />

                  <div className={styles.landing__preview__container}>
                    <label>Preview</label>
                    <div className={styles.landing__preview__button__wrapper}>
                      <SecondaryButton
                        className={styles.landing__preview__button}
                        label="Preview"
                        onClick={handleClickPromocodePreview}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </AccordionSection>
          </fieldset>

          <fieldset ref={emailsRef} className={styles.section__emails}>
            <div className={styles.profile_container}>
              <h4 className={styles.section__title}>Email to Prospect</h4>
              {help.find((h) => h.tag === 'prospect_setup_emails') && (
                <HelpModal
                  className={styles.helpModal}
                  help={help.find((h) => h.tag === 'prospect_setup_emails')}
                >
                  <p>
                    {help.find((h) => h.tag === 'prospect_setup_emails')?.text}
                  </p>
                </HelpModal>
              )}
            </div>
            <div className={styles.emails_to__wrapper}>
              {[
                // MESSAGE.P__HELP,
                MESSAGE.P__CONFIRM,
                // MESSAGE.P__PROMO_CODE,
                // MESSAGE.P__REGISTRATION,
              ].map((TEMPLATE, index) => (
                <div className={styles.field__email_to} key={index}>
                  <Checkbox
                    className={styles.field__email_to__checkbox}
                    label={TEMPLATE.LABEL}
                    disabled={isLocked}
                    readOnly={true}
                    isChecked={true}
                    help={help.find((h) => h.tag === TEMPLATE.HELP)}
                    helpChildren={
                      <p>{help.find((h) => h.tag === TEMPLATE.HELP)?.text}</p>
                    }
                  />

                  <TertiaryButton
                    disabled={isLocked}
                    onClick={() => handleClickEmailEditor(TEMPLATE)}
                  >
                    <PencilIcon
                      className={clsx(
                        styles.button__email__icon,
                        isLocked && styles['button__email__icon--disabled']
                      )}
                    />
                    Edit
                  </TertiaryButton>

                  <SecondaryButton
                    onClick={() =>
                      window
                        .open(
                          `${PATH.PREVIEW__PS__EMAIL}?campaignId=${campaignId}&messageType=${TEMPLATE.TYPE}`,
                          '_blank'
                        )
                        .focus()
                    }
                  >
                    <EyeVisibleColorIcon
                      className={clsx(
                        styles.button__email__icon,
                        isLocked && styles['button__email__icon--disabled']
                      )}
                    />
                    View
                  </SecondaryButton>
                </div>
              ))}
            </div>
          </fieldset>

          <div className={styles.section__footer}>
            <SecondaryButton
              className={styles.button__back}
              label="Back"
              onClick={onBack}
            />

            <PrimaryButton
              className={styles.button__next}
              label="Save"
              onClick={() => handleSubmitOnly()}
            />

            <PrimaryButton
              className={styles.button__next}
              label="Save & Next"
              onClick={() => handleClickSubmit(true)}
            />

            <SecondaryButton
              label="Save & Close"
              onClick={() => handleClickSubmit(false)}
            />
          </div>
        </form>
      </div>

      <EmailTemplateEditorModal
        campaignId={campaignId}
        setupType="PS"
        {...emailTemplateToEdit}
        show={isEmailEditorOpened}
        onClose={() => setEmailEditorOpened(false)}
        isLocked={isLocked}
      />

      {activeCamp && (
        <PSPromoCodeModal
          show={showPromoCode}
          linkToRegistrationPage={formik.values.linkToRegistrationPage}
          enrollInstructions={formik.values.enrollInstructions}
          promocode={promocode}
          enrollPopupBtnTxt={formik.values.enrollPopupBtnTxt}
          enrollPopupHeading={formik.values.enrollPopupHeading}
          camp={activeCamp}
          onClose={() => setShowPromoCode(false)}
        />
      )}

      <LoadingOverlay isLoading={isLoading} />
    </>
  )
}

export default ProspectSetup
